/**
 * Shared Calculation Engine — PFD Analysis Platform
 * 
 * Single source of truth for:
 * - Biological FCR calculation
 * - Biomass calculations (start, closing, harvest, mortality, gain)
 * - Fish count resolution order
 * - Sample anomaly detection
 * 
 * Used by: Spreadsheet Uploader, Growth vs Model, and any future modules.
 */

// ============================================================================
// BIOLOGICAL FCR CALCULATION
// ============================================================================

/**
 * Calculate biological FCR from production data.
 * 
 * Formula: FCR = totalFeedKg / biomassGain
 * Where: biomassGain = closingBiomass + harvestBiomass + mortalityBiomass - startBiomass
 * 
 * @param {Object} params
 * @param {number} params.totalFeedKg - Total feed given in kg
 * @param {number} params.startWeightG - Starting average weight in grams
 * @param {number} params.startCount - Starting fish count
 * @param {number} params.currentWeightG - Current/closing average weight in grams
 * @param {number} params.currentCount - Current fish count (after mortality/harvest deductions)
 * @param {number} params.harvestBiomassKg - Total harvested biomass in kg (default 0)
 * @param {number} params.mortalityBiomassKg - Total mortality biomass in kg (default 0)
 * @returns {Object} { fcr, startBiomassKg, closingBiomassKg, harvestBiomassKg, mortalityBiomassKg, biomassGainKg }
 */
export const calculateBiologicalFCR = ({
  totalFeedKg,
  startWeightG,
  startCount,
  currentWeightG,
  currentCount,
  harvestBiomassKg = 0,
  mortalityBiomassKg = 0,
}) => {
  const startBiomassKg = (startWeightG * startCount) / 1000;
  const closingBiomassKg = (currentWeightG * currentCount) / 1000;
  const biomassGainKg = closingBiomassKg + harvestBiomassKg + mortalityBiomassKg - startBiomassKg;

  if (biomassGainKg <= 0 || totalFeedKg <= 0) {
    return {
      fcr: null,
      startBiomassKg,
      closingBiomassKg,
      harvestBiomassKg,
      mortalityBiomassKg,
      biomassGainKg,
    };
  }

  const fcr = totalFeedKg / biomassGainKg;

  return {
    fcr: Math.round(fcr * 100) / 100,
    startBiomassKg: Math.round(startBiomassKg * 100) / 100,
    closingBiomassKg: Math.round(closingBiomassKg * 100) / 100,
    harvestBiomassKg: Math.round(harvestBiomassKg * 100) / 100,
    mortalityBiomassKg: Math.round(mortalityBiomassKg * 100) / 100,
    biomassGainKg: Math.round(biomassGainKg * 100) / 100,
  };
};


// ============================================================================
// FISH COUNT RESOLUTION
// ============================================================================

/**
 * Resolve the effective fish count using a single explicit priority order.
 * 
 * Priority:
 * 1. Validated FCR/algebraic adjusted fish count (strongest — derived from feed data)
 * 2. Confirmed jump-based adjusted fish count (trajectory analysis)
 * 3. Reported fish count fallback (user-declared)
 * 
 * @param {Object} params
 * @param {number|null} params.fcrAlgebraicCount - Fish count from FCR algebraic solution
 * @param {number|null} params.jumpBasedCount - Fish count from trajectory jump detection
 * @param {number} params.reportedCount - User-declared starting fish count
 * @param {string|null} params.fcrConfidence - Confidence of FCR solution ('high','moderate','low')
 * @param {string|null} params.jumpConfidence - Confidence of jump detection ('high','moderate','low')
 * @returns {Object} { effectiveCount, source, confidence, missingFish }
 */
export const resolveFishCount = ({
  fcrAlgebraicCount = null,
  jumpBasedCount = null,
  reportedCount,
  fcrConfidence = null,
  jumpConfidence = null,
}) => {
  // Priority 1: FCR algebraic (if positive and reasonable)
  if (fcrAlgebraicCount != null && fcrAlgebraicCount > 0 && fcrAlgebraicCount < reportedCount) {
    return {
      effectiveCount: Math.round(fcrAlgebraicCount),
      source: 'fcr_algebraic',
      confidence: fcrConfidence || 'moderate',
      missingFish: Math.round(reportedCount - fcrAlgebraicCount),
    };
  }

  // Priority 2: Jump-based (if positive and reasonable)
  if (jumpBasedCount != null && jumpBasedCount > 0 && jumpBasedCount < reportedCount) {
    return {
      effectiveCount: Math.round(jumpBasedCount),
      source: 'jump_detection',
      confidence: jumpConfidence || 'low',
      missingFish: Math.round(reportedCount - jumpBasedCount),
    };
  }

  // Priority 3: Reported count fallback
  return {
    effectiveCount: reportedCount,
    source: 'reported',
    confidence: 'reported',
    missingFish: 0,
  };
};


// ============================================================================
// SAMPLE ANOMALY DETECTION
// ============================================================================

/**
 * Flag suspicious sample data points without removing them.
 * 
 * Checks for:
 * - Unrealistic upward jumps (sudden weight gain beyond biological limits)
 * - Jump followed by reversal (spike then drop)
 * - Extreme deviation from adjacent samples
 * 
 * @param {Array} samples - Array of { weight_g, date, day, ... }
 * @param {string} species - Species name for threshold calibration
 * @returns {Array} Array of anomaly flags: { sampleIndex, type, severity, detail, recommendation }
 */
export const detectSampleAnomalies = (samples, species = 'Seabream') => {
  if (!samples || samples.length < 2) return [];

  const anomalies = [];

  // Species-specific daily growth ceiling (%/day max realistic SGR)
  const maxDailySGR = species === 'Tilapia' ? 0.04 : 0.03; // 3-4% per day max

  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const curr = samples[i];

    if (!prev.weight_g || !curr.weight_g || prev.weight_g <= 0 || curr.weight_g <= 0) continue;

    const dayGap = (curr.day || 0) - (prev.day || 0);
    if (dayGap <= 0) continue;

    const growthRatio = curr.weight_g / prev.weight_g;
    const dailySGR = Math.log(growthRatio) / dayGap;
    const percentChange = ((curr.weight_g - prev.weight_g) / prev.weight_g) * 100;

    // --- Check 1: Unrealistic upward jump ---
    // Max theoretical growth over the period
    const maxExpectedRatio = Math.exp(maxDailySGR * dayGap);
    if (growthRatio > maxExpectedRatio * 1.5) {
      anomalies.push({
        sampleIndex: i,
        sampleNumber: i + 1,
        type: 'unrealistic_jump',
        severity: growthRatio > maxExpectedRatio * 2.5 ? 'high' : 'moderate',
        detail: `Weight jumped ${percentChange.toFixed(0)}% in ${dayGap} days (${prev.weight_g.toFixed(0)}g → ${curr.weight_g.toFixed(0)}g). Maximum expected growth for this period is ~${((maxExpectedRatio - 1) * 100).toFixed(0)}%.`,
        recommendation: 'Verify this sample measurement. If confirmed, it may indicate a sampling error or batch mix-up.',
      });
    }

    // --- Check 2: Weight decrease (negative growth) ---
    if (curr.weight_g < prev.weight_g * 0.85) {
      anomalies.push({
        sampleIndex: i,
        sampleNumber: i + 1,
        type: 'weight_decrease',
        severity: curr.weight_g < prev.weight_g * 0.7 ? 'high' : 'moderate',
        detail: `Weight dropped ${Math.abs(percentChange).toFixed(0)}% (${prev.weight_g.toFixed(0)}g → ${curr.weight_g.toFixed(0)}g). Fish do not normally lose significant weight unless under extreme stress.`,
        recommendation: 'Verify sample accuracy. This may indicate a measurement error, different fish cohort being sampled, or severe environmental stress.',
      });
    }

    // --- Check 3: Jump followed by reversal (spike then drop) ---
    if (i >= 2) {
      const prevPrev = samples[i - 2];
      if (prevPrev.weight_g && prevPrev.weight_g > 0) {
        const prevJumpRatio = prev.weight_g / prevPrev.weight_g;
        const currDropRatio = curr.weight_g / prev.weight_g;

        // Previous sample spiked up > 40%, then current dropped back > 20%
        if (prevJumpRatio > 1.4 && currDropRatio < 0.8) {
          anomalies.push({
            sampleIndex: i - 1,
            sampleNumber: i,
            type: 'spike_reversal',
            severity: 'high',
            detail: `Sample ${i} spiked ${((prevJumpRatio - 1) * 100).toFixed(0)}% then Sample ${i + 1} dropped ${((1 - currDropRatio) * 100).toFixed(0)}%. This spike-reversal pattern strongly suggests a measurement error at Sample ${i}.`,
            recommendation: 'The middle sample in this sequence is likely unreliable. Consider excluding it from analysis.',
          });
        }
      }
    }

    // --- Check 4: Extreme deviation from adjacent samples ---
    if (i < samples.length - 1) {
      const next = samples[i + 1];
      if (next.weight_g && next.weight_g > 0) {
        // Interpolate expected weight at current position
        const totalDays = (next.day || 0) - (prev.day || 0);
        const partialDays = (curr.day || 0) - (prev.day || 0);
        if (totalDays > 0 && partialDays > 0) {
          const interpolatedWeight = prev.weight_g + (next.weight_g - prev.weight_g) * (partialDays / totalDays);
          const deviationFromInterpolated = Math.abs(curr.weight_g - interpolatedWeight) / interpolatedWeight;

          if (deviationFromInterpolated > 0.35) {
            // Only flag if not already flagged by other checks
            const alreadyFlagged = anomalies.some(a => a.sampleIndex === i);
            if (!alreadyFlagged) {
              anomalies.push({
                sampleIndex: i,
                sampleNumber: i + 1,
                type: 'extreme_deviation',
                severity: deviationFromInterpolated > 0.5 ? 'high' : 'moderate',
                detail: `Weight (${curr.weight_g.toFixed(0)}g) deviates ${(deviationFromInterpolated * 100).toFixed(0)}% from the expected trajectory between adjacent samples (expected ~${interpolatedWeight.toFixed(0)}g).`,
                recommendation: 'This sample appears inconsistent with surrounding data. Resample to confirm.',
              });
            }
          }
        }
      }
    }
  }

  return anomalies;
};


/**
 * Apply anomaly impact to confidence scoring.
 * Reduces confidence based on the number and severity of flagged anomalies.
 * 
 * @param {number} baseScore - Original confidence/quality score (0-100)
 * @param {Array} anomalies - Array from detectSampleAnomalies()
 * @returns {Object} { adjustedScore, penalty, reason }
 */
export const applyAnomalyPenalty = (baseScore, anomalies) => {
  if (!anomalies || anomalies.length === 0) {
    return { adjustedScore: baseScore, penalty: 0, reason: null };
  }

  let penalty = 0;
  const highCount = anomalies.filter(a => a.severity === 'high').length;
  const modCount = anomalies.filter(a => a.severity === 'moderate').length;

  penalty += highCount * 15;
  penalty += modCount * 8;

  const adjustedScore = Math.max(0, baseScore - penalty);
  const reason = `${anomalies.length} sample anomal${anomalies.length === 1 ? 'y' : 'ies'} detected (${highCount} high, ${modCount} moderate) — confidence reduced by ${penalty} points`;

  return { adjustedScore, penalty, reason };
};
